import React, { useState, useEffect } from "react";
import axios from "axios";
import "./dashboard.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { API_BASE_URL } from "../config";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalDeposit: 0,
    totalconversations: 0,
    totalUsers: 0,
  });

  const [fromDate, setFromDate] = useState("2025-07-01");
  const [toDate, setToDate] = useState("2025-07-16");
  const [newUsersToday, setNewUsersToday] = useState(0);
  const [topRequests, setTopRequests] = useState([]);
  const [last7DaysRequests, setLast7DaysRequests] = useState([]);
  const [activitySummary, setActivitySummary] = useState([]);
  const [usersByState, setUsersByState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [industryStats, setIndustryStats] = useState([]);
  const [businessStats, setBusinessStats] = useState([]);

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#aa00ff",
    "#e91e63",
  ];

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/summary`, {
        params: {
          from_date: fromDate,
          to_date: toDate,
        },
      });

      const data = res.data;

      setStats({
        totalDeposit: data.total_deposit || 0,
        totalUsers: data.total_users || 0,
        totalconversations: data.total_tokens || 0,
      });

      setNewUsersToday(data.new_users_today || 0);
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherData = async () => {
    try {
      const [topRes, lastRes, activityRes, stateRes, industryRes, businessRes] =
        await Promise.all([
          axios.get(`${API_BASE_URL}/top-requests`),
          axios.get(`${API_BASE_URL}/last-7-days-requests`),
          axios.get(`${API_BASE_URL}/activity-summary`),
          axios.get(`${API_BASE_URL}/users-by-state`),
          axios.get(`${API_BASE_URL}/industry-stats`),
          axios.get(`${API_BASE_URL}/business-stats`),
        ]);

      setTopRequests(topRes.data || []);
      setLast7DaysRequests(lastRes.data || []);
      setActivitySummary(activityRes.data || []);
      setUsersByState(stateRes.data || []);
      setIndustryStats(industryRes.data || []);
      setBusinessStats(businessRes.data || []);
    } catch (err) {
      console.error("Failed to fetch charts:", err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOtherData();
  }, []);

  const handleReset = () => {
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 7 * 86400000)
      .toISOString()
      .slice(0, 10);
    setFromDate(weekAgo);
    setToDate(today);
  };

  return (
    <div className="dashboard-wrapper">
      {/* Date Filter */}
      <div className="date-filter stat-card">
        <label>From</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <label>To</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <button className="submit-btn" onClick={fetchStats}>
          Submit
        </button>
        <button className="reset-btn" onClick={handleReset}>
          Reset
        </button>
      </div>

      {loading && <p>Loading data...</p>}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <p>Total Conversations</p>
          <h3>{stats.totalconversations}</h3>
        </div>
        <div className="stat-card">
          <p>Total Deposit</p>
          <h3>{stats.totalDeposit}</h3>
        </div>
        <div className="stat-card">
          <p>Total Users</p>
          <h3>{stats.totalUsers}</h3>
        </div>
        <div className="stat-card">
          <p>New Users Today</p>
          <h3>{newUsersToday}</h3>
        </div>
      </div>

      {/* Charts: Requests */}
      <div className="charts-wrapper">
        {/* <div className="chart-card">
          <h4>Top 10 Requests</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topRequests}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div> */}
        <div className="chart-card">
          <h4>Industry Specific Stats</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={industryStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3f51b5" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h4>Business Level Stats</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={businessStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#009688" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card">
          <h4>Last 7 Days Total Request</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={last7DaysRequests}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="requests" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Charts */}
      <div className="charts-wrapper">
        <div className="chart-card">
          <h4>Most Frequent Activities</h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={activitySummary}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {activitySummary.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h4>Users by State</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={usersByState}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="state" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="users" fill="#ff9800" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
